import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Button } from "@volca/ui/components/ui/button";
import { cn } from "@volca/ui/lib/utils";
import { useJobInvoice } from "@/accounts-payable/query/job-invoice";
import type { ApDocumentType } from "@/accounts-payable/types";
import { stInvoiceUrl, stJobUrl } from "@/accounts-payable/utils";
import { extractErrorMessage, isApiErrorResponse } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { useInView } from "@/core/hooks";

export const MatchedJobLink = ({
  jobId,
  jobNumber,
  documentType = "invoice",
  className,
}: {
  jobId: number;
  jobNumber?: string | null;
  documentType?: ApDocumentType;
  className?: string;
}) => {
  const { businessId } = useBusiness();
  const [ref, inView] = useInView<HTMLSpanElement>({ triggerOnce: true });
  const invoice = useJobInvoice(
    businessId,
    jobId,
    documentType === "invoice" && inView,
  );

  const error = invoice.isError
    ? isApiErrorResponse(invoice.error)
      ? invoice.error
      : extractErrorMessage(invoice.error)
    : null;

  const displayJobNumber = invoice.data?.job_number || jobNumber || jobId;
  const invoiceNumber = invoice.data?.number || invoice.data?.id;

  return (
    <span
      ref={ref}
      className={cn("inline-flex items-center gap-1", className)}
      aria-busy={documentType === "invoice" && invoice.isFetching}
    >
      {documentType === "receipt" ||
      invoice.data?.id == null ||
      error?.status === 404 ? (
        <ServiceTitanIconLink
          url={stJobUrl(jobId)}
          label={`Open job #${displayJobNumber} in ServiceTitan`}
          stopPropagation
        >
          Job #{displayJobNumber}
        </ServiceTitanIconLink>
      ) : (
        <ServiceTitanIconLink
          url={stInvoiceUrl(invoice.data.id)}
          label={`Open invoice #${invoiceNumber} in ServiceTitan`}
          stopPropagation
        >
          Inv #{invoiceNumber}
        </ServiceTitanIconLink>
      )}
      {documentType === "invoice" && error && error.status !== 404 && (
        <Button
          variant="link"
          size="xs"
          title={error.message}
          loading={invoice.isFetching}
          onClick={(event) => {
            event.stopPropagation();
            void invoice.refetch();
          }}
        >
          Retry ST invoice
        </Button>
      )}
    </span>
  );
};
